/**
 * Routes of the new fan site. Old paths from today's site redirect to their new home, so every link already sent in
 * emails/texts keeps working if v2 ever replaces cutiecaryn.com.
 * Stripe Elements + today's toast provider wrap everything because the reused payment windows need them.
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { ToastProvider } from '@/components/ui/ToastContainer'
import { useAuth } from '@/features/auth'
import { usePresenceBeat } from '@/hooks/usePresenceBeat'
import { LegalPage } from '@/pages/LegalPage'
import { DashLayout } from '~/app/DashLayout'
import { FEATURES } from '~/config/mode'
import { Member, Waiting } from '~/app/Member'
import { useAuthReady } from '~/app/session'
import { Enter } from '~/pages/Enter'
import { Apply } from '~/pages/Apply'
import { SignedOut } from '~/pages/SignedOut'
import { Home } from '~/pages/Home'
import { Text } from '~/pages/Text'
import { Gallery } from '~/pages/Gallery'
import { VaultPage } from '@/pages/VaultPage'
import { VaultSkeleton } from '~/pages/VaultSkeleton'
import { Inbox } from '~/pages/Inbox'
import { Diary } from '~/pages/Diary'
import { Settings } from '~/pages/Settings'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_missing')

function Routing() {
  const { isAuthenticated } = useAuth()
  const ready = useAuthReady()
  usePresenceBeat(isAuthenticated) // today's "online" ring in the admin inbox; never affects the fan
  if (!ready) return <div className="cw-dash"><Waiting label="One moment…" /></div>

  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? <Navigate to="/home" replace /> : <Enter />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <Enter />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/signed-out" element={<SignedOut />} />
      <Route path="/terms" element={<div className="tw-scope"><LegalPage kind="terms" /></div>} />
      <Route path="/privacy" element={<div className="tw-scope"><LegalPage kind="privacy" /></div>} />
      <Route path="/refund-policy" element={<div className="tw-scope"><LegalPage kind="refunds" /></div>} />

      <Route element={<DashLayout />}>
        <Route element={<Member />}>
          <Route path="/home" element={<Home />} />
          <Route path="/text" element={<Text />} />
          <Route path="/gallery" element={<Gallery />} />
          {/* owner 11-04: the Vault must work exactly like today's - today's Vault page (same calls, same unlocks), new look */}
          <Route path="/vault" element={<main className="wrap v2-legacy-vault" id="main"><VaultSkeleton /><div className="tw-scope"><VaultPage /></div></main>} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/diary" element={FEATURES.diary ? <Diary /> : <Navigate to="/home" replace />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/:page" element={<Settings />} />
        </Route>
      </Route>

      {/* today's paths */}
      <Route path="/welcome" element={<Navigate to="/home" replace />} />
      <Route path="/messages" element={<Navigate to={`/text${location.search}`} replace />} />
      <Route path="/messaging" element={<Navigate to="/text" replace />} />
      <Route path="/account" element={<Navigate to="/settings/account" replace />} />
      <Route path="/subscription" element={<Navigate to="/settings/subscription" replace />} />
      <Route path="/payment-methods" element={<Navigate to="/settings/wallet" replace />} />
      <Route path="/wallet" element={<Navigate to="/settings/wallet" replace />} />
      {/* today's pages that v2 does not have (owner 11-04: "newsroom doesn't need to exist, same with status… remove the
          worlds section for now"; group chat / poke / mail are not linked anywhere today) - old links land on Home / Email */}
      <Route path="/newsroom" element={<Navigate to="/inbox" replace />} />
      <Route path="/status" element={<Navigate to="/home" replace />} />
      <Route path="/vip" element={<Navigate to="/home" replace />} />
      <Route path="/worlds/*" element={<Navigate to="/home" replace />} />
      <Route path="/poke" element={<Navigate to="/home" replace />} />
      <Route path="/groupchat" element={<Navigate to="/home" replace />} />
      <Route path="/mail" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <Elements stripe={stripePromise}>
        <BrowserRouter>
          <Routing />
        </BrowserRouter>
      </Elements>
    </ToastProvider>
  )
}
